import { ExternalLinkIcon, XCircleIcon } from "@heroicons/react/outline";
import { Clock, Coins, GitBranch, ListTree, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import SidebarCard from "./SidebarCard";

import { normalizeToRequestLogStatus } from "@/components/StructuredSearch/config/filter-value-type-behavior";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { TRACES_SIDEBAR_URL_NAMESPACE } from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { applyStructuredSearchStateToSearchParams } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import {
  formatTraceCost,
  formatTraceCount,
  formatTraceDuration,
} from "@/components/TracesSearch/utils";
import { ContextMenu, ContextMenuItems, ContextMenuVariant } from "@/components/ui/ContextMenu";
import { Skeleton } from "@/components/ui/skeleton";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { EntityType } from "@/queries/folder-entities/types";
import { useCloseTrace } from "@/queries/spans";
import { Span } from "@/types/spans";
import type { TraceListItem } from "@/types/traces";
import { STATUS_STYLES } from "@/types/request-status";
import { getDuration } from "@/utils/duration";
import { formatTimestampUTC } from "@/utils/timestamp";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { cn } from "@/lib/utils";

const formatListValues = (values: (number | string)[] | null | undefined): string[] =>
  values?.map(String).filter(Boolean) ?? [];

const ENTITY_LIST_GAP_PX = 8;
const entityHydrationSkeletonClassName = "from-gray-100 via-gray-200 to-gray-100";

interface EntityListItem {
  type: EntityType;
  label: string;
  value: string;
}

const TruncatedEntityValues = ({
  items,
  className,
}: {
  items: EntityListItem[];
  className?: string;
}) => {
  const recalcKey = items.map(item => `${item.type}:${item.value}`).join("|");
  const { containerRef, measureRef, visibleCount } = useTruncatedList({
    itemCount: items.length,
    keepMinOne: true,
    gapPx: ENTITY_LIST_GAP_PX,
    overflowIndicatorWidth: 28,
    recalcKey,
  });

  if (items.length === 0) {
    return null;
  }

  const displayCount = Math.max(1, visibleCount);
  const overflowCount = items.length - displayCount;
  const isClampedToOne = visibleCount < 1;

  const renderItem = (item: EntityListItem, allowTruncate = false) => (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-normal text-gray-600",
        allowTruncate ? "min-w-0 max-w-full" : "shrink-0",
      )}
    >
      <EntityIcon type={item.type} size="xs" className="shrink-0" />
      <span className={allowTruncate ? "truncate" : ""}>{item.value}</span>
    </span>
  );

  const hiddenTooltip = items
    .slice(displayCount)
    .map(item => `${item.label}: ${item.value}`)
    .join(", ");

  const measureClassName =
    "pointer-events-none invisible absolute left-0 top-0 flex flex-nowrap items-center gap-2";
  const containerClassName = cn(
    "relative flex min-w-0 max-w-full flex-nowrap items-center gap-2 overflow-hidden",
    className,
  );

  if (overflowCount > 0 && isClampedToOne) {
    return (
      <div ref={containerRef} className={containerClassName}>
        <div ref={measureRef} className={measureClassName} aria-hidden="true">
          {items.map(item => (
            <span key={`${item.type}-${item.value}`} className="shrink-0">
              {renderItem(item)}
            </span>
          ))}
        </div>
        <BasicTooltip content={items[0].value}>
          <span className="min-w-0 flex-1 overflow-hidden">{renderItem(items[0], true)}</span>
        </BasicTooltip>
        <BasicTooltip content={hiddenTooltip}>
          <span className="shrink-0 text-xs text-gray-500">+{overflowCount}</span>
        </BasicTooltip>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={containerClassName}>
      <div ref={measureRef} className={measureClassName} aria-hidden="true">
        {items.map(item => (
          <span key={`${item.type}-${item.value}`} className="shrink-0">
            {renderItem(item)}
          </span>
        ))}
      </div>
      {items.slice(0, displayCount).map(item =>
        overflowCount > 0 ? (
          <BasicTooltip key={`${item.type}-${item.value}`} content={item.value}>
            <span className="min-w-0 shrink-0">{renderItem(item)}</span>
          </BasicTooltip>
        ) : (
          <span key={`${item.type}-${item.value}`} className="min-w-0 shrink-0">
            {renderItem(item)}
          </span>
        ),
      )}
      {overflowCount > 0 && (
        <BasicTooltip content={hiddenTooltip}>
          <span className="shrink-0 text-xs text-gray-500">+{overflowCount}</span>
        </BasicTooltip>
      )}
    </div>
  );
};

const SpanCard = ({ span, trace }: { span?: Span; trace?: TraceListItem }) => {
  const traceItem = trace ?? null;
  const spanItem = span ?? null;
  const traceIdValue = traceItem?.trace_id ?? spanItem?.trace_id ?? "";
  const startTime = traceItem?.start ?? spanItem?.start ?? "";
  const endTime = traceItem?.end ?? spanItem?.end ?? null;
  const displayName = traceItem?.name ?? spanItem?.name ?? "Untitled trace";
  const { traceId } = useParams();
  const location = useLocation();
  const sidebarStore = useStructuredSearchStore();
  const userContext = useUser();
  const userToken = useUserToken();
  const closeTrace = useCloseTrace(userToken, userContext?.activeWorkspaceId ?? 0);

  const isEditorRun = spanItem?.attributes?.is_editor_run === true;
  const isTestRun = spanItem?.attributes?.is_test_run === "true";
  const requestStatus = traceItem?.status ? normalizeToRequestLogStatus(traceItem.status) : null;
  const statusLabel = requestStatus === "ERROR" ? "Error" : "Warning";
  const active = traceIdValue === traceId;
  const timestamp = startTime
    ? formatTimestampUTC(startTime, { includeSeconds: true, showRelative: false })
    : "—";
  const duration = traceItem
    ? formatTraceDuration(traceItem.duration_ms)
    : getDuration(startTime, endTime);
  const depth = formatTraceCount(traceItem?.depth ?? null);
  const cost = formatTraceCost(traceItem?.total_cost_usd ?? null);
  const preservedParams = applyStructuredSearchStateToSearchParams(
    new URLSearchParams(location.search.replace(/^\?/, "")),
    {
      filters: sidebarStore.filters,
      dateRange: sidebarStore.dateRange,
      q: sidebarStore.q,
      pageIndex: sidebarStore.pageIndex,
      pageSize: sidebarStore.pageSize,
      sorting: sidebarStore.sorting,
    },
    { namespace: TRACES_SIDEBAR_URL_NAMESPACE },
  ).toString();
  const tracePath = `/workspace/${userContext?.activeWorkspaceId}/traces/${traceIdValue}${
    preservedParams ? `?${preservedParams}` : ""
  }`;

  const contextMenuItems: ContextMenuItems<Span | TraceListItem>[] = useMemo(
    () => [
      {
        id: "open-in-new-tab",
        label: "Open in a new tab",
        icon: ExternalLinkIcon,
        variant: ContextMenuVariant.PRIMARY,
        onClick: () => window.open(tracePath, "_blank", "noopener,noreferrer"),
      },
      {
        id: "close-trace",
        label: "Close trace",
        icon: XCircleIcon,
        variant: ContextMenuVariant.DESTRUCTIVE,
        onClick: () =>
          closeTrace.mutate(traceIdValue, {
            onSuccess: () => displayToast("Trace closed", ToastType.success),
            onError: error => displayErrorToast(error),
          }),
      },
    ],
    [tracePath, closeTrace, traceIdValue],
  );

  const { toolItems, promptItems, workflowItems } = useMemo(() => {
    const tools = traceItem?.tool_names?.filter(Boolean) ?? [];
    const prompts = formatListValues(traceItem?.hydrated_prompt_names ?? traceItem?.prompt_ids);
    const workflows = formatListValues(
      traceItem?.hydrated_workflow_names ?? traceItem?.workflow_ids,
    );

    return {
      toolItems: tools.map(value => ({ type: EntityType.TOOL, label: "Tools", value })),
      promptItems: prompts.map(value => ({ type: EntityType.PROMPT, label: "Prompts", value })),
      workflowItems: workflows.map(value => ({
        type: EntityType.WORKFLOW,
        label: "Workflows",
        value,
      })),
    };
  }, [
    traceItem?.tool_names,
    traceItem?.hydrated_prompt_names,
    traceItem?.prompt_ids,
    traceItem?.hydrated_workflow_names,
    traceItem?.workflow_ids,
  ]);

  const spansFooterLabel =
    traceItem?.span_count !== null && traceItem?.span_count !== undefined
      ? `${traceItem.span_count.toLocaleString()} spans`
      : "—";

  const isEntityHydrating = traceItem?.entity_hydration_status === "pending";
  const hasEntityHydrationSkeleton =
    isEntityHydrating && (promptItems.length > 0 || workflowItems.length > 0);
  const hasEntities =
    toolItems.length > 0 ||
    promptItems.length > 0 ||
    workflowItems.length > 0 ||
    hasEntityHydrationSkeleton;
  const entityGroupCount =
    (toolItems.length > 0 ? 1 : 0) +
    (hasEntityHydrationSkeleton ? 1 : 0) +
    (!hasEntityHydrationSkeleton && promptItems.length > 0 ? 1 : 0) +
    (!hasEntityHydrationSkeleton && workflowItems.length > 0 ? 1 : 0);
  const entityGroupClassName =
    entityGroupCount === 1 ? "min-w-0 max-w-full w-full" : "min-w-0 shrink";

  return (
    <ContextMenu contextMenuItems={contextMenuItems} data={(spanItem ?? traceItem)!} modal={false}>
      <SidebarCard
        to={tracePath}
        id={traceIdValue}
        active={active}
        requestLogHoverCue
        requestLogSelectionCue
        className="!space-y-0 duration-300 ease-out animate-in fade-in-0"
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 overflow-hidden">
            <ListTree className="h-3.5 w-3.5 shrink-0 text-gray-500" />
            <div className="relative min-w-0 flex-1 truncate text-xs font-semibold text-gray-900">
              <OverflowDisplayTooltip content={displayName} className="block truncate">
                {displayName}
              </OverflowDisplayTooltip>
              {(isEditorRun || isTestRun) && (
                <BasicTooltip content={isEditorRun ? "Editor Run" : "Test Run"}>
                  <span className="absolute -right-2 -top-0.5 font-semibold text-yellow-600">
                    *
                  </span>
                </BasicTooltip>
              )}
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {endTime === null && <Loader2 className="h-3 w-3 animate-spin text-blue-500" />}
              {requestStatus && requestStatus !== "SUCCESS" && (
                <BasicTooltip content={statusLabel}>
                  <span
                    className="relative flex h-3 w-3 items-center justify-center"
                    aria-label={`${statusLabel} trace`}
                  >
                    <span
                      className={`absolute h-3 w-3 rounded-full opacity-10 ${STATUS_STYLES[requestStatus].dot}`}
                    />
                    <span
                      className={`relative h-1.5 w-1.5 rounded-full ${STATUS_STYLES[requestStatus].dot}`}
                    />
                  </span>
                </BasicTooltip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs font-normal text-gray-500">
            <span className="inline-flex shrink-0 items-center gap-1 tabular-nums">
              <Coins className="h-3 w-3 shrink-0 text-gray-400" />
              <span>{cost}</span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 tabular-nums">
              <Clock className="h-3 w-3 shrink-0 text-gray-400" />
              <span>{duration}</span>
            </span>
          </div>

          {hasEntities && (
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 overflow-hidden">
              {toolItems.length > 0 && (
                <TruncatedEntityValues items={toolItems} className={entityGroupClassName} />
              )}
              {hasEntityHydrationSkeleton && (
                <span className="inline-flex min-w-0 shrink items-center gap-1">
                  <Skeleton
                    className={`${entityHydrationSkeletonClassName} shrink-0`}
                    width="12px"
                    height="12px"
                    borderRadius="3px"
                  />
                  <Skeleton
                    className={entityHydrationSkeletonClassName}
                    width="72px"
                    height="10px"
                    borderRadius="3px"
                  />
                </span>
              )}
              {!hasEntityHydrationSkeleton && promptItems.length > 0 && (
                <TruncatedEntityValues items={promptItems} className={entityGroupClassName} />
              )}
              {!hasEntityHydrationSkeleton && workflowItems.length > 0 && (
                <TruncatedEntityValues items={workflowItems} className={entityGroupClassName} />
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-x-3 text-xs font-normal text-gray-500">
            <BasicTooltip content={`${spansFooterLabel} · depth ${depth}`}>
              <span className="inline-flex min-w-0 items-center gap-1 truncate">
                <GitBranch className="h-3 w-3 shrink-0 text-gray-400" />
                <span>{spansFooterLabel}</span>
              </span>
            </BasicTooltip>
            <div title={startTime} className="shrink-0 whitespace-nowrap">
              {timestamp}
            </div>
          </div>
        </div>
      </SidebarCard>
    </ContextMenu>
  );
};

export default SpanCard;
